import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";

import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from "lucide-react";
import { cn } from "../lib/utils";

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "fixed top-4 right-4 z-[9999] flex flex-col gap-2 w-full max-w-[420px]",
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const getVariantStyles = (variant: string) => {
  switch (variant) {
    case "success":
      return "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-success/40 dark:bg-success-surface dark:text-success";
    case "error":
      return "border-red-200 bg-red-50 text-red-900 dark:border-danger/40 dark:bg-danger-surface dark:text-danger";
    case "warning":
      return "border-amber-200 bg-amber-50 text-amber-900 dark:border-warning/40 dark:bg-warning-surface dark:text-warning";
    case "info":
      return "border-blue-200 bg-blue-50 text-blue-900 dark:border-primary-border dark:bg-brand/40 dark:text-link";
    default:
      return "border-gray-200 bg-white text-gray-900 dark:border-border dark:bg-surface dark:text-foreground";
  }
};

const getVariantIcon = (variant: string) => {
  switch (variant) {
    case "success":
      return (
        <CheckCircle className="h-5 w-5 text-emerald-500 dark:text-emerald-400" />
      );
    case "error":
      return <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />;
    case "warning":
      return (
        <AlertTriangle className="h-5 w-5 text-amber-500 dark:text-amber-400" />
      );
    case "info":
      return <Info className="h-5 w-5 text-blue-500 dark:text-link" />;
    default:
      return null;
  }
};

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & {
    variant?: "default" | "success" | "error" | "warning" | "info";
    icon?: React.ReactNode;
    showProgress?: boolean;
    duration?: number;
  }
>(
  (
    {
      className,
      variant = "default",
      icon,
      showProgress = true,
      duration,
      children,
      ...props
    },
    ref,
  ) => {
    const [progress, setProgress] = React.useState(100);
    const progressDuration = duration || 5000;

    React.useEffect(() => {
      if (!showProgress) return;

      const startTime = Date.now();
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 100 - (elapsed / progressDuration) * 100);
        setProgress(remaining);

        if (remaining <= 0) {
          clearInterval(interval);
        }
      }, 10);

      return () => clearInterval(interval);
    }, [showProgress, progressDuration]);

    return (
      <ToastPrimitives.Root
        ref={ref}
        className={cn(
          "group pointer-events-auto relative flex w-full items-start gap-3",
          "overflow-hidden rounded-xl border shadow-lg",
          "transition-all duration-300 ease-out",
          "data-[swipe=cancel]:translate-x-0",
          "data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]",
          "data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]",
          "data-[swipe=move]:transition-none",
          "data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-full",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-bottom-full",
          getVariantStyles(variant),
          className,
        )}
        {...props}
      >
        <div className="flex items-start gap-3 flex-1 p-4">
          {icon || getVariantIcon(variant)}
          <div className="flex-1 min-w-0">{children}</div>
        </div>

        {showProgress && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/10 dark:bg-white/10">
            <div
              className={`h-full transition-all duration-100 ease-linear ${
                variant === "success"
                  ? "bg-emerald-500"
                  : variant === "error"
                    ? "bg-red-500"
                    : variant === "warning"
                      ? "bg-amber-500"
                      : variant === "info"
                        ? "bg-blue-500 dark:bg-link"
                        : "bg-blue-500 dark:bg-link"
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </ToastPrimitives.Root>
    );
  },
);
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
      "group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive",
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "absolute right-1 top-1 rounded-md p-1 opacity-0 transition-opacity hover:bg-black/5 dark:hover:bg-white/10 focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100",
      className,
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("text-sm font-semibold leading-tight", className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("text-sm opacity-90 mt-1", className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;
type ToastActionElement = React.ReactElement<
  React.ComponentProps<typeof ToastAction>
>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
